import { styled } from '@mui/material/styles';
import { Box, Tooltip, Typography } from '@mui/material';
import { AnchorHTMLAttributes, ClassAttributes, useMemo } from 'react';
import { ExtraProps } from 'react-markdown';
import { VIEW_ARTICLE } from 'constants/routes';
import { PromptSummaryReferenceType } from './hooks/useBuildWiseGPTHistoryQuery';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';

type FormatLinkProps = ClassAttributes<HTMLAnchorElement> &
  AnchorHTMLAttributes<HTMLAnchorElement> &
  ExtraProps & {
    metaData: PromptSummaryReferenceType[];
    metaDataOrder: {
      originalValue: number;
      orderValue: number;
    }[];
  };

function FormatLink(props: FormatLinkProps) {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const { children, className, node, metaData, metaDataOrder, ...rest } = props;
  const referenceMetaInfo = useMemo(
    () => metaData[Number(props.href?.replace('#source', '')) || 0],
    [metaData, props.href],
  );
  const refIndex = useMemo(
    () =>
      (metaDataOrder.find(
        (item) => item.originalValue === (Number(props.href?.replace('#source', '')) || 0),
      )?.orderValue || 0) + 1,
    [props.href, metaDataOrder],
  );

  if (children === 'source') {
    return (
      <Link
        {...(rest as any)}
        {...(referenceMetaInfo
          ? {
              to: `${VIEW_ARTICLE.replace(':articleId', referenceMetaInfo?.id || '')}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
              target: '_blank',
              state: {
                ...state,
                article: referenceMetaInfo,
              },
            }
          : {
              as: 'span',
              display: 'inline',
            })}
      >
        <Tooltip
          PopperProps={{
            sx: (theme) => ({
              display: referenceMetaInfo?.title ? 'block' : 'none',
              '& .MuiTooltip-tooltip': {
                maxWidth: 500,
                p: 2,
                borderRadius: '8px',
                marginBottom: '8px !important',
                backgroundColor: theme.palette.custom.background,
                border: `1px solid ${theme.palette.custom.divider}`,
              },
            }),
          }}
          title={
            <Link
              to={`${VIEW_ARTICLE.replace(':articleId', referenceMetaInfo?.id || '')}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}
              state={{ ...state, article: referenceMetaInfo }}
              sx={(theme) => ({
                fontSize: 14,
                color: theme.palette.custom.tuna,
                '&:hover': {
                  color: theme.palette.custom.link,
                },
              })}
            >
              <Box
                sx={(theme) => ({
                  display: 'inline',
                  px: 0.5,
                  mr: 1,
                  border: `1px solid ${theme.palette.custom.divider}`,
                  borderRadius: '4px',
                  fontSize: 11,
                  color: theme.palette.custom.tuna,
                  backgroundColor: theme.palette.custom.background,
                  '&:hover': {
                    color: theme.palette.custom.link,
                    borderColor: theme.palette.custom.link,
                    backgroundColor: theme.palette.custom.mariner01,
                  },
                })}
              >
                {refIndex}
              </Box>
              {referenceMetaInfo?.title}
            </Link>
          }
        >
          <sup>
            <Box
              sx={(theme) => ({
                display: 'inline',
                px: 0.5,
                border: `1px solid ${theme.palette.custom.divider}`,
                borderRadius: '4px',
                fontSize: 11,
                color: theme.palette.custom.tuna,
                backgroundColor: theme.palette.custom.background,
                '&:hover': {
                  color: theme.palette.custom.link,
                  borderColor: theme.palette.custom.link,
                  backgroundColor: theme.palette.custom.mariner01,
                },
              })}
              className={`citation ${props.className?.replaceAll('citation', '')}`}
              id={String(Number(props.href?.replace('#source', '')) || 0)}
            >
              {refIndex}
            </Box>
          </sup>
        </Tooltip>
      </Link>
    );
  }

  return (
    <Link {...(rest as any)} href={props.href} target="_blank" className={props.className}>
      {children}
    </Link>
  );
}

const Link = styled(RouterLink, { label: 'Link' })(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  textDecoration: 'none',
  cursor: 'pointer',
  color: theme.palette.custom.link,
}));

export default FormatLink;
